import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FiEdit3, FiTrash2, FiPlus, FiX, FiCheck } from 'react-icons/fi';



const getToolboxToken  = () => localStorage.getItem('toolboxToken') || '';
const getNcToken   = () => localStorage.getItem('amsToken') || '';
/* ── Types ── */
interface Agent {
  name: string;
  agentId: string;
  userId: string;
  isAgencyUser: boolean;
}

interface CredentialRow {
  transactionId: string;
  name: string;
  apiKey: string;
  secretKey: string;
  agents: string;
  agentInfoJson: Agent[];
}

/* ── Shared input style ── */
const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 8,
  padding: '8px 12px',
  color: 'var(--text-primary)',
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  color: 'var(--text-secondary)',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  marginBottom: 5,
  display: 'block',
};

const errStyle: React.CSSProperties = {
  color: '#ef4444',
  fontSize: 11,
  marginTop: 4,
};

const primaryBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'linear-gradient(135deg, #ec4899, #A200FF)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '8px 18px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const ghostBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'rgba(255,255,255,0.06)',
  color: 'var(--text-secondary)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const cardStyle: React.CSSProperties = {
  background: 'var(--surface-glass)',
  border: '1px solid var(--border-glass)',
  borderRadius: 12,
  padding: '18px 20px',
};

/* ── Empty credential form ── */
const emptyForm = () => ({
  transactionId: '',
  name: '',
  apiKey: '',
  secretKey: '',
  selectedAgents: [] as Agent[],
});

/* ────────────────────────────────────────── */

const LinqsESignatureView: React.FC = () => {
  const navigate = useNavigate();

  /* Subscription */
  const [isSubscribed, setIsSubscribed]         = useState(false);
  const [isCardAdded, setIsCardAdded]           = useState(false);
  const [subLoading, setSubLoading]             = useState(false);
  const [subToggling, setSubToggling]           = useState(false);
  const [showSubConfirm, setShowSubConfirm]     = useState(false);

  /* Agents from NowCerts */
  const [agents, setAgents]                     = useState<Agent[]>([]);
  const [agentSearch, setAgentSearch]           = useState('');

  /* Credential list */
  const [rows, setRows]                         = useState<CredentialRow[]>([]);
  const [histLoading, setHistLoading]           = useState(false);

  /* Add form */
  const [showForm, setShowForm]                 = useState(false);
  const [form, setForm]                         = useState(emptyForm());
  const [saving, setSaving]                     = useState(false);
  const [errors, setErrors]                     = useState<Record<string, string>>({});

  /* Edit modal */
  const [showEditModal, setShowEditModal]       = useState(false);
  const [editForm, setEditForm]                 = useState(emptyForm());
  const [editErrors, setEditErrors]             = useState<Record<string, string>>({});
  const [editSaving, setEditSaving]             = useState(false);
  const [agentEditSearch, setAgentEditSearch]   = useState('');

  /* Setup tabs */
  const [activeTab, setActiveTab]               = useState<'howToSetup' | 'howItWorks'>('howToSetup');

  /* Toast */
  const [toast, setToast]                       = useState<{ msg: string; type: 'success' | 'error' | 'warn' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' | 'warn' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const authErr = (status: number) => {
    if (status === 401) navigate('/');
  };

  /* ── Fetch subscription status ── */
  const fetchSubscription = () => {
    setSubLoading(true);
    axios.get(`${(process.env.REACT_APP_STRIPEPAYMENTGATEWAY || '')}/api/Billing/subscription`, {
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then((res) => {
        setIsCardAdded(res.data.subscriptionDetails.isCardAdded);
        setIsSubscribed(res.data.subscriptionDetails.isRabbitSignSubscribed);
      })
      .catch((err) => authErr(err.response?.status))
      .finally(() => setSubLoading(false));
  };

  /* ── Fetch agents from NowCerts ── */
  const fetchAgents = () => {
    axios.get(`${(process.env.REACT_APP_NOWCERTSAPI          || '')}/api/nowcert/agencylist?nowCertsToken=${getNcToken()}`, {
      headers: {
        Authorization: `Bearer ${getToolboxToken()}`,
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        const list: Agent[] = res.data.map((item: any) => ({
          name: (item.firstName || item.lastName)
            ? `${item.firstName} ${item.lastName}`
            : item.fullName,
          agentId: item.agentId,
          userId: item.userId,
          isAgencyUser: item.isAgencyUser,
        }));
        setAgents(list);
      })
      .catch((err) => authErr(err.response?.status));
  };

  /* ── Fetch credential history ── */
  const fetchHistory = () => {
    setHistLoading(true);
    axios.get(`${(process.env.REACT_APP_DataFabric_Url      || '')}/api/RabbitSign/history`, {
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then((res) => {
        const raw = res.data.responseJson ?? [];
        if (raw.length > 0) {
          setRows(raw.map((item: any) => {
            const agentInfoJson: Agent[] = JSON.parse(item.agents);
            return {
              transactionId: item.transactionId,
              name: item.name,
              apiKey: item.apiKey,
              secretKey: item.secretKey,
              agents: agentInfoJson.map((a) => a.name).join(', '),
              agentInfoJson,
            };
          }));
        } else {
          setRows([]);
          setShowForm(true);
        }
      })
      .catch((err) => authErr(err.response?.status))
      .finally(() => setHistLoading(false));
  };

  /* ── Sync credentials to NowCerts ── */
  const syncToNowCerts = (agentList: Agent[], apiKey: string, secretKey: string) => {
    axios.post(
      `${(process.env.REACT_APP_NOWCERTSAPI          || '')}/api/nowcert/addkeyforagent?nowCertsToken=${getNcToken()}`,
      {
        Settings: agentList.map((a) => ({
          userId: a.userId,
          apiKey,
          apiSecret: secretKey,
          isAgencyUser: a.isAgencyUser,
        })),
      },
      {
        headers: {
          Authorization: `Bearer ${getToolboxToken()}`,
          'Content-Type': 'application/json',
        },
      },
    )
      .then(() => showToast('Credentials synced to NowCerts successfully.'))
      .catch(() => showToast('Failed to sync credentials to NowCerts.', 'error'));
  };

  /* ── On mount ── */
  useEffect(() => {
    fetchSubscription();
    fetchAgents();
    fetchHistory();
  }, []);

  /* ── Validate form ── */
  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim())           e.name      = 'Key name is required.';
    if (!form.apiKey.trim())         e.apiKey    = 'API key is required.';
    if (!form.secretKey.trim())      e.secretKey = 'Secret key is required.';
    if (form.selectedAgents.length === 0) e.agents = 'Select at least one agent.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── Save (insert) ── */
  const handleSave = () => {
    if (!validate()) return;
    setSaving(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url      || '')}/api/RabbitSign/insert`,
      {
        Target: 'RABBIT-SIGN',
        name: form.name,
        apiKey: form.apiKey,
        secretkey: form.secretKey,
        agents: JSON.stringify(form.selectedAgents),
      },
      {
        headers: {
          Authorization: `Bearer ${getToolboxToken()}`,
          'Content-Type': 'application/json',
        },
      },
    )
      .then(() => {
        showToast('Credentials added successfully.');
        setShowForm(false);
        setForm(emptyForm());
        fetchHistory();
        syncToNowCerts(form.selectedAgents, form.apiKey, form.secretKey);
      })
      .catch((err) => {
        authErr(err.response?.status);
        showToast('Failed to save credentials. Please try again.', 'error');
      })
      .finally(() => setSaving(false));
  };

  /* ── Update ── */
  const validateEdit = () => {
    const e: Record<string, string> = {};
    if (!editForm.name.trim())                e.name      = 'Key name is required.';
    if (!editForm.apiKey.trim())              e.apiKey    = 'API key is required.';
    if (!editForm.secretKey.trim())           e.secretKey = 'Secret key is required.';
    if (editForm.selectedAgents.length === 0) e.agents    = 'Select at least one agent.';
    setEditErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUpdate = () => {
    if (!validateEdit()) return;
    setEditSaving(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url      || '')}/api/RabbitSign/update`,
      {
        transactionId: editForm.transactionId,
        name: editForm.name,
        apiKey: editForm.apiKey,
        secretkey: editForm.secretKey,
        agents: JSON.stringify(editForm.selectedAgents),
      },
      {
        headers: {
          Authorization: `Bearer ${getToolboxToken()}`,
          'Content-Type': 'application/json',
        },
      },
    )
      .then(() => {
        showToast('Credentials updated successfully.');
        setShowEditModal(false);
        setEditForm(emptyForm());
        fetchHistory();
        syncToNowCerts(editForm.selectedAgents, editForm.apiKey, editForm.secretKey);
      })
      .catch((err) => {
        authErr(err.response?.status);
        showToast('Failed to update credentials. Please try again.', 'error');
      })
      .finally(() => setEditSaving(false));
  };

  /* ── Delete ── */
  const handleDelete = (row: CredentialRow) => {
    axios.delete(`${(process.env.REACT_APP_DataFabric_Url      || '')}/api/RabbitSign/isdelete?transactionId=${row.transactionId}`, {
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then(() => {
        showToast('Credentials deleted successfully.');
        fetchHistory();
      })
      .catch((err) => {
        authErr(err.response?.status);
        showToast('Failed to delete credentials.', 'error');
      });
  };

  /* ── Open edit modal ── */
  const openEdit = (row: CredentialRow) => {
    setEditForm({
      transactionId: row.transactionId,
      name: row.name,
      apiKey: row.apiKey,
      secretKey: row.secretKey,
      selectedAgents: row.agentInfoJson,
    });
    setEditErrors({});
    setAgentEditSearch('');
    setShowEditModal(true);
  };

  /* ── Toggle agent selection ── */
  const toggleAgent = (agent: Agent) => {
    const already = form.selectedAgents.some((a) => a.agentId === agent.agentId);
    setForm((f) => ({
      ...f,
      selectedAgents: already
        ? f.selectedAgents.filter((a) => a.agentId !== agent.agentId)
        : [...f.selectedAgents, agent],
    }));
  };

  /* ── Toggle subscription ── */
  const handleSubscribeConfirm = () => {
    setShowSubConfirm(false);
    setSubToggling(true);
    axios.post(
      `${(process.env.REACT_APP_STRIPEPAYMENTGATEWAY || '')}/api/Billing/rabbitsign-subscribe?subscribe=${!isSubscribed}`,
      null,
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then((res) => {
        if (res.data.inserted > 0) {
          showToast(isSubscribed ? 'Subscription deactivated.' : 'Subscription activated.');
          fetchSubscription();
        } else {
          showToast('No payment method found. Please add a payment method.', 'warn');
        }
      })
      .catch((err) => {
        authErr(err.response?.status);
        showToast('Failed to update subscription.', 'error');
      })
      .finally(() => setSubToggling(false));
  };

  const filteredAgents = agentSearch.trim()
    ? agents.filter((a) => a.name.toLowerCase().includes(agentSearch.toLowerCase()))
    : agents;

  const filteredEditAgents = agentEditSearch.trim()
    ? agents.filter((a) => a.name.toLowerCase().includes(agentEditSearch.toLowerCase()))
    : agents;

  const toggleEditAgent = (agent: Agent) => {
    const already = editForm.selectedAgents.some((a) => a.agentId === agent.agentId);
    setEditForm((f) => ({
      ...f,
      selectedAgents: already
        ? f.selectedAgents.filter((a) => a.agentId !== agent.agentId)
        : [...f.selectedAgents, agent],
    }));
  };

  const toastColor = toast?.type === 'success' ? '#10b981' : toast?.type === 'warn' ? '#f59e0b' : '#ef4444';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'relative' }}>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: `${toastColor}18`,
          border: `1px solid ${toastColor}44`,
          borderRadius: 10, padding: '10px 16px',
          color: toastColor, fontSize: 13, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        }}>
          {toast.type === 'success' ? <FiCheck size={14} /> : <FiX size={14} />}
          {toast.msg}
        </div>
      )}

      {/* ── Subscription confirm overlay ── */}
      {showSubConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9000,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--surface-glass)',
            border: '1px solid var(--border-glass)',
            borderRadius: 14, padding: '24px 28px',
            width: 360, boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
          }}>
            <div style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 700, marginBottom: 10 }}>
              {isSubscribed ? 'Unsubscribe from Linqs E-Signature?' : 'Subscribe to Linqs E-Signature?'}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7, margin: '0 0 20px' }}>
              {isSubscribed
                ? 'Your agents will lose access to RabbitSign e-signature functionality.'
                : 'This will activate your $10/month subscription for unlimited e-signing.'}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={ghostBtn} onClick={() => setShowSubConfirm(false)}>Cancel</button>
              <button style={primaryBtn} onClick={handleSubscribeConfirm}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit credentials modal ── */}
      {showEditModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9000,
          background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#0c0a1d',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 14, padding: '24px 28px',
            width: 580, maxWidth: '95vw',
            boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 700 }}>Edit Credentials</div>
              <button
                onClick={() => { setShowEditModal(false); setEditForm(emptyForm()); setEditErrors({}); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4 }}
              >
                <FiX size={18} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Key Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  style={{ ...inputStyle, borderColor: editErrors.name ? '#ef4444' : undefined }}
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Agency Master Key"
                />
                {editErrors.name && <div style={errStyle}>{editErrors.name}</div>}
              </div>
              <div>
                <label style={labelStyle}>API Key <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  style={{ ...inputStyle, borderColor: editErrors.apiKey ? '#ef4444' : undefined }}
                  value={editForm.apiKey}
                  onChange={(e) => setEditForm((f) => ({ ...f, apiKey: e.target.value }))}
                  placeholder="RabbitSign API key"
                />
                {editErrors.apiKey && <div style={errStyle}>{editErrors.apiKey}</div>}
              </div>
              <div>
                <label style={labelStyle}>Secret Key <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  style={{ ...inputStyle, borderColor: editErrors.secretKey ? '#ef4444' : undefined }}
                  value={editForm.secretKey}
                  onChange={(e) => setEditForm((f) => ({ ...f, secretKey: e.target.value }))}
                  placeholder="RabbitSign secret key"
                />
                {editErrors.secretKey && <div style={errStyle}>{editErrors.secretKey}</div>}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>
                Assign Agents <span style={{ color: '#ef4444' }}>*</span>
                {editForm.selectedAgents.length > 0 && (
                  <span style={{ color: '#A200FF', marginLeft: 8, textTransform: 'none', fontWeight: 500 }}>
                    {editForm.selectedAgents.length} selected
                  </span>
                )}
              </label>
              <input
                style={{ ...inputStyle, marginBottom: 8 }}
                value={agentEditSearch}
                onChange={(e) => setAgentEditSearch(e.target.value)}
                placeholder="Search agents…"
              />
              <div style={{
                maxHeight: 160, overflowY: 'auto',
                background: 'rgba(0,0,0,0.2)', borderRadius: 8,
                border: editErrors.agents ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.07)',
              }}>
                {filteredEditAgents.length === 0 ? (
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12, padding: '12px 14px', textAlign: 'center' }}>
                    No agents found
                  </div>
                ) : filteredEditAgents.map((agent) => {
                  const selected = editForm.selectedAgents.some((a) => a.agentId === agent.agentId);
                  return (
                    <div
                      key={agent.agentId}
                      onClick={() => toggleEditAgent(agent)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 14px', cursor: 'pointer',
                        background: selected ? 'rgba(162,0,255,0.08)' : 'transparent',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        transition: 'background 0.15s',
                      }}
                    >
                      <div style={{
                        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                        border: selected ? '1px solid #A200FF' : '1px solid rgba(255,255,255,0.2)',
                        background: selected ? 'rgba(162,0,255,0.2)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {selected && <FiCheck size={10} color="#A200FF" />}
                      </div>
                      <span style={{ color: 'var(--text-primary)', fontSize: 13 }}>{agent.name}</span>
                    </div>
                  );
                })}
              </div>
              {editErrors.agents && <div style={errStyle}>{editErrors.agents}</div>}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button style={ghostBtn} onClick={() => { setShowEditModal(false); setEditForm(emptyForm()); setEditErrors({}); }}>
                Cancel
              </button>
              <button
                style={{ ...primaryBtn, opacity: editSaving ? 0.7 : 1 }}
                onClick={handleUpdate}
                disabled={editSaving}
              >
                {editSaving ? '…' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header card ── */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700 }}>Rabbit Sign</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Get Unlimited E-Signing</div>
          </div>
          {subLoading ? (
            <div style={{ width: 46, height: 24, borderRadius: 99, background: 'rgba(255,255,255,0.06)', animation: 'tbx-shimmer 1.5s infinite' }} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 12px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                background: isSubscribed ? 'rgba(16,185,129,0.12)' : 'rgba(255,255,255,0.05)',
                border: isSubscribed ? '1px solid rgba(16,185,129,0.3)' : '1px solid rgba(255,255,255,0.1)',
                color: isSubscribed ? '#10b981' : 'rgba(255,255,255,0.35)',
              }}>
                {isSubscribed ? '✓ Active' : 'Inactive'}
              </span>
              <button
                onClick={() => isCardAdded ? setShowSubConfirm(true) : navigate('/paymentmethod')}
                disabled={subToggling}
                style={{
                  ...ghostBtn,
                  fontSize: 11,
                  padding: '5px 12px',
                  opacity: subToggling ? 0.6 : 1,
                }}
              >
                {subToggling ? '…' : isSubscribed ? 'Unsubscribe' : 'Subscribe — $10/mo'}
              </button>
            </div>
          )}
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.75, margin: 0 }}>
          Quickly send the same document for signature over and over. There is no limit on how many templates you can create and use. Quickly send the same document for signature over and over. There is no limit on how many templates you can create and use.
        </p>
      </div>

      {/* ── Setup steps ── */}
      <div style={{ display: 'flex', gap: 16 }}>
        {/* Left — step-by-step instructions */}
        <div style={{ flex: 1 }}>
          <div style={cardStyle}>
            <div>
              <strong style={{ color: 'var(--text-primary)', fontSize: 13 }}>Access Rabbit sign:</strong>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                Step 1 : Visit{' '}
                <a href="https://www.rabbitsign.com/" target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>RabbitSign</a>
                {' '}portal to create an account.
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                Step 2 : Log in to your existing account or create a new one.
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <strong style={{ color: 'var(--text-primary)', fontSize: 13 }}>Enable Developer Console:</strong>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                Step 1 : Navigate to your "My Account" page.
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                Step 2 : Locate the "Developer API" area..
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <strong style={{ color: 'var(--text-primary)', fontSize: 13 }}>Generate API Key:</strong>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                Step 1 : Click on the "Developer Console" link.
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                Step 2 : Add payment method and accept{' '}
                <a href="https://www.rabbitsign.com/developerTos.html" target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>
                  Developer Terms of Service
                </a>.
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                Step 3 : Click the button to Create Key.
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <strong style={{ color: 'var(--text-primary)', fontSize: 13 }}>Configure Momentum Toolbox:</strong>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                Step 1 : Copy the generated API key and secret key.
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                Step 2 : Open the Momentum Toolbox settings.
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                Step 3 : Paste the keys into the appropriate fields.
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
                Step 4 : Select the agents you want to assign to the RabbitSign account.
              </div>
            </div>
          </div>
        </div>

        {/* Right — How to Setup / How it Works tabs */}
        <div style={{ flex: 1 }}>
          <div style={{ ...cardStyle, padding: '16px 20px' }}>
            {/* Tab headers */}
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 14 }}>
              {(['howToSetup', 'howItWorks'] as const).map((tab) => {
                const label = tab === 'howToSetup' ? 'How to Setup' : 'How it Works';
                const isActive = activeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      background: 'none',
                      border: 'none',
                      borderBottom: isActive ? '2px solid #A200FF' : '2px solid transparent',
                      color: isActive ? '#A200FF' : 'var(--text-secondary)',
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 400,
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      marginBottom: -1,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            {activeTab === 'howToSetup' && (
              <iframe
                src="https://www.loom.com/embed/3544ff2808e44cfa98a7ab1dbfa454f9?sid=4ffcb361-0588-45b5-af29-548eeb8fa2e5"
                allowFullScreen
                title="How to Setup"
                style={{ width: '100%', height: 360, borderRadius: 8, display: 'block', border: 'none' }}
              />
            )}
            {activeTab === 'howItWorks' && (
              <iframe
                src="https://www.youtube.com/embed/LDtFmXKGzGg"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="How it Works"
                style={{ width: '100%', height: 360, borderRadius: 8, display: 'block', border: 'none' }}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Credentials card ── */}
      <div style={cardStyle}>
        {!histLoading && rows.length === 0 && !showForm && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 700 }}>Already have a RabbitSign account? <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>Enter your credentials here.</span></div>
            <button style={primaryBtn} onClick={() => { setForm(emptyForm()); setErrors({}); setShowForm(true); }}>
              <FiPlus size={14} /> Add Credentials
            </button>
          </div>
        )}

        {/* ── Add / Edit form ── */}
        {showForm && (
          <div style={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 10, padding: '16px 18px', marginBottom: 20,
          }}>
            <div style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
              Add New Credentials
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
              {/* Key Name */}
              <div>
                <label style={labelStyle}>Key Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  style={{ ...inputStyle, borderColor: errors.name ? '#ef4444' : undefined }}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Agency Master Key"
                />
                {errors.name && <div style={errStyle}>{errors.name}</div>}
              </div>

              {/* API Key */}
              <div>
                <label style={labelStyle}>API Key <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  style={{ ...inputStyle, borderColor: errors.apiKey ? '#ef4444' : undefined }}
                  value={form.apiKey}
                  onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
                  placeholder="RabbitSign API key"
                />
                {errors.apiKey && <div style={errStyle}>{errors.apiKey}</div>}
              </div>

              {/* Secret Key */}
              <div>
                <label style={labelStyle}>Secret Key <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  style={{ ...inputStyle, borderColor: errors.secretKey ? '#ef4444' : undefined }}
                  value={form.secretKey}
                  onChange={(e) => setForm((f) => ({ ...f, secretKey: e.target.value }))}
                  placeholder="RabbitSign secret key"
                />
                {errors.secretKey && <div style={errStyle}>{errors.secretKey}</div>}
              </div>
            </div>

            {/* Agent selection */}
            <div>
              <label style={labelStyle}>
                Assign Agents <span style={{ color: '#ef4444' }}>*</span>
                {form.selectedAgents.length > 0 && (
                  <span style={{ color: '#ec4899', marginLeft: 8, textTransform: 'none', fontWeight: 500 }}>
                    {form.selectedAgents.length} selected
                  </span>
                )}
              </label>
              <input
                style={{ ...inputStyle, marginBottom: 8 }}
                value={agentSearch}
                onChange={(e) => setAgentSearch(e.target.value)}
                placeholder="Search agents…"
              />
              <div style={{
                maxHeight: 180, overflowY: 'auto',
                background: 'rgba(0,0,0,0.2)', borderRadius: 8,
                border: errors.agents ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.07)',
              }}>
                {filteredAgents.length === 0 ? (
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12, padding: '12px 14px', textAlign: 'center' }}>
                    No agents found
                  </div>
                ) : filteredAgents.map((agent) => {
                  const selected = form.selectedAgents.some((a) => a.agentId === agent.agentId);
                  return (
                    <div
                      key={agent.agentId}
                      onClick={() => toggleAgent(agent)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 14px', cursor: 'pointer',
                        background: selected ? 'rgba(236,72,153,0.08)' : 'transparent',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        transition: 'background 0.15s',
                      }}
                    >
                      <div style={{
                        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                        border: selected ? '1px solid #ec4899' : '1px solid rgba(255,255,255,0.2)',
                        background: selected ? 'rgba(236,72,153,0.2)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {selected && <FiCheck size={10} color="#ec4899" />}
                      </div>
                      <span style={{ color: 'var(--text-primary)', fontSize: 13 }}>{agent.name}</span>
                    </div>
                  );
                })}
              </div>
              {errors.agents && <div style={errStyle}>{errors.agents}</div>}
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button style={ghostBtn} onClick={() => { setShowForm(false); setForm(emptyForm()); setErrors({}); }}>
                Cancel
              </button>
              <button
                style={{ ...primaryBtn, opacity: saving ? 0.7 : 1 }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? '…' : 'Add'}
              </button>
            </div>
          </div>
        )}

        {/* ── Credentials table ── */}
        {histLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{ height: 44, borderRadius: 8, background: 'rgba(255,255,255,0.04)', animation: 'tbx-shimmer 1.5s infinite' }} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          !showForm && (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13, padding: '24px 0' }}>
              No credentials stored yet. Click "Add Credentials" to get started.
            </div>
          )
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Key Name', 'API Key', 'Secret Key', 'Agents', 'Actions'].map((h) => (
                    <th key={h} style={{
                      color: 'var(--text-secondary)', fontWeight: 600, fontSize: 11,
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                      padding: '8px 12px', textAlign: 'left',
                      borderBottom: '1px solid rgba(255,255,255,0.07)',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.transactionId} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ padding: '10px 12px', color: 'var(--text-primary)' }}>{row.name}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: 12 }}>
                      {row.apiKey.length > 20 ? `${row.apiKey.slice(0, 20)}…` : row.apiKey}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: 12 }}>
                      {row.secretKey.length > 20 ? `${row.secretKey.slice(0, 20)}…` : row.secretKey}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{row.agents}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          title="Edit"
                          onClick={() => openEdit(row)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#60a5fa', padding: 4 }}
                        >
                          <FiEdit3 size={15} />
                        </button>
                        <button
                          title="Delete"
                          onClick={() => handleDelete(row)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4 }}
                        >
                          <FiTrash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinqsESignatureView;

